import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  useEntryRules,
  useCreateEntryRule,
  useUpdateEntryRule,
  useSetMakerFlag,
  useHidePairFromOpener,
  useManualConfirmedPairs,
  useEntryPanic,
  useEntryPanicRecent
} from '../api/useEntry';
import { useSystemConfig } from '../api/useSystemConfig';
import { useEntrySocket } from '../hooks/useEntrySocket';
import { useLiveState } from '../live/useLiveStore';
import { EnginePositionDrawer } from '../components/EnginePositionDrawer';
import {
  EntryTabByPath,
  CreateEntrySumRuleParams
} from '../components/entry/EntryTabByPath';
import { TAB_FLAGS, FLAG_TO_SIDES } from '../components/entry/entryMath';
import { StopStartOpenerButtons } from '../components/entry/StopStartOpenerButtons';
import {
  EntryRule,
  UpdateEntryRuleDto,
  ManualConfirmedPair,
  ENTRY_HARD_CAP_GROSS,
  DEFAULT_TARGET_TNA_PERCENT,
  MakerFlag
} from '../types/entry.types';

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export const EntryPage = () => {
  const { data: pairsData, isLoading } = useManualConfirmedPairs();
  const { data: rules } = useEntryRules();
  const { data: systemConfig } = useSystemConfig();
  const createRuleMutation = useCreateEntryRule();
  const updateRuleMutation = useUpdateEntryRule();
  const setMakerFlagMutation = useSetMakerFlag();
  const hidePairMutation = useHidePairFromOpener();
  const panicMutation = useEntryPanic();
  const panicRecentMutation = useEntryPanicRecent();
  const { isConnected, store } = useEntrySocket();

  // Two side-specific tabs. Tab A = poly YES + kalshi NO; Tab B = poly NO
  // + kalshi YES. Default to A.
  const [tab, setTab] = useState<'a' | 'b'>('a');

  // Free-text search over pair titles. Matches `pairName`, `kalshiTitle`,
  // `polymarketTitle` (case-insensitive substring) so the operator can narrow
  // a long pair list to the one they care about. Empty / whitespace-only
  // string disables the filter. Mirrors the unwinder's UnwindingPage search.
  const [search, setSearch] = useState('');

  // "Engine position" drawer — the live engine's believed contracts per
  // (path, platform) for the selected rule, read off the same liveState the
  // row already holds.
  const [enginePos, setEnginePos] = useState<{ ruleId: string; pairName: string } | null>(null);
  const handleOpenEnginePosition = useCallback(
    (ruleId: string, pairName: string) => setEnginePos({ ruleId, pairName }),
    [],
  );
  const handleCloseEnginePosition = useCallback(() => setEnginePos(null), []);

  // Drawer reads the selected rule's believed position reactively off the
  // live store so it stays fresh as fills arrive (the page itself no longer
  // re-renders on every WS tick). Subscribes to a placeholder key when the
  // drawer is closed — harmless, returns undefined.
  const enginePosState = useLiveState(store, enginePos?.ruleId ?? '');

  const pairs: ManualConfirmedPair[] = useMemo(() => pairsData ?? [], [pairsData]);

  // Search filter: case-insensitive substring match across the three title
  // fields a pair can carry. Empty / whitespace-only query is a no-op
  // (returns the full list unchanged). Applied before the list reaches the
  // tab so both tabs share the same filter.
  const visiblePairs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pairs;
    return pairs.filter((p) => {
      const name = (p.pairName ?? '').toLowerCase();
      const kt = (p.kalshiTitle ?? '').toLowerCase();
      const pt = (p.polymarketTitle ?? '').toLowerCase();
      return name.includes(q) || kt.includes(q) || pt.includes(q);
    });
  }, [pairs, search]);

  const globalEnabled = Array.isArray(rules) && rules.some(r => r.status === 'active');

  const sumRules = useMemo(
    () => (rules ?? []).filter(r => r.ruleType === 'sum_of_sides'),
    [rules]
  );

  // All rules keyed by pairId — used by the row's status indicator so it
  // works regardless of the active tab.
  const anyRulesByPairId = useMemo(() => {
    const map = new Map<string, EntryRule>();
    for (const rule of rules ?? []) {
      if (!rule.pairId) continue;
      const existing = map.get(rule.pairId);
      if (!existing) {
        map.set(rule.pairId, rule);
        continue;
      }
      const incomingActive = rule.status === 'active';
      const existingActive = existing.status === 'active';
      if (incomingActive && !existingActive) {
        map.set(rule.pairId, rule);
      }
    }
    return map;
  }, [rules]);

  const handleCreateSumRule = useCallback(
    async ({
      pair,
      makerFlag,
      entryStrategy,
      tabPath,
      thresholdSum,
      size,
      maxVolumeContracts
    }: CreateEntrySumRuleParams) => {
      if (!pair._id) return;
      // Hard-cap layer 3: never POST a rule with thresholdSum >= 0.999.
      if (!(thresholdSum > 0 && thresholdSum < ENTRY_HARD_CAP_GROSS)) {
        return;
      }
      // Derive sides from the chosen maker flag, or from the tab's path
      // when activating via TT (no maker flag) or a pre-rule paused-
      // create. The backend re-runs the same derivation; this just
      // gives it a head start.
      const effectiveFlag: MakerFlag = makerFlag ?? TAB_FLAGS[tabPath].poly;
      const { polySide, kalshiSide } = FLAG_TO_SIDES[effectiveFlag];
      await createRuleMutation.mutateAsync({
        pairId: pair._id,
        pairName: pair.pairName,
        expiryDate: pair.expiryDate || new Date().toISOString(),
        ruleType: 'sum_of_sides',
        thresholdSum,
        kalshiConfig: pair.kalshi?.ticker
          ? {
              side: kalshiSide,
              ticker: pair.kalshi.ticker,
              contractsToBuy: size,
              targetTnaPercent: DEFAULT_TARGET_TNA_PERCENT
            }
          : undefined,
        polymarketConfig:
          pair.polymarket?.yesTokenId && pair.polymarket?.noTokenId
            ? {
                side: polySide,
                yesTokenId: pair.polymarket.yesTokenId,
                noTokenId: pair.polymarket.noTokenId,
                conditionId:
                  pair.polymarket.conditionId ?? pair.polymarket.yesTokenId,
                contractsToBuy: size,
                targetTnaPercent: DEFAULT_TARGET_TNA_PERCENT
              }
            : undefined,
        maxVolumeContracts,
        volumePeriod: 'day',
        kalshiEnabled: true,
        polymarketEnabled: true,
        ...(makerFlag ? { [makerFlag]: true } : {}),
        ...(entryStrategy ? { entryStrategy } : {}),
      });
    },
    [createRuleMutation]
  );

  const handleSetMakerFlag = useCallback(
    async (
      rule: EntryRule,
      flag: MakerFlag,
      value: boolean,
      clearOthers?: MakerFlag[],
    ) => {
      await setMakerFlagMutation.mutateAsync({
        id: rule._id,
        flag,
        value,
        clearOthers,
      });
    },
    [setMakerFlagMutation],
  );

  const handleHidePair = useCallback(
    async (pair: ManualConfirmedPair) => {
      if (!pair._id) return;
      await hidePairMutation.mutateAsync(pair._id);
    },
    [hidePairMutation],
  );

  const handleUpdateRule = useCallback(
    async (rule: EntryRule, dto: UpdateEntryRuleDto) => {
      try {
        await updateRuleMutation.mutateAsync({ id: rule._id, dto });
        return true;
      } catch {
        return false;
      }
    },
    [updateRuleMutation]
  );

  return (
    <Box sx={{ px: 2, pt: 1, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Title bar */}
      <Stack direction="row" alignItems="center" sx={{ mb: 0.5, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ color: 'common.white' }}>
          Opening
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={1} alignItems="center">
          {systemConfig && (
            <Chip
              label={`Engine: ${systemConfig.openerEngine === 'rust' ? 'Rust' : 'Node'}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
          <Chip
            icon={<FiberManualRecordIcon sx={{ fontSize: '0.6rem !important' }} />}
            label={isConnected ? 'Live' : 'Disconnected'}
            size="small"
            color={isConnected ? 'success' : 'default'}
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
          <FormControlLabel
            control={<Switch size="small" checked={globalEnabled} disabled />}
            label="Global"
            sx={{ '& .MuiTypography-root': { fontSize: '0.75rem' }, mr: 0 }}
          />
          <StopStartOpenerButtons />
          <Button
            variant="contained"
            color="warning"
            size="small"
            startIcon={<WarningAmberIcon />}
            onClick={() => panicRecentMutation.mutate()}
            sx={{ fontSize: '0.7rem' }}
            disabled={panicRecentMutation.isLoading}
            title="Cancel buys + pause rules active in the last 24h. Orders on unrelated markets are NOT touched."
          >
            {panicRecentMutation.isLoading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              'PANIC 24H'
            )}
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<WarningAmberIcon />}
            onClick={() => panicMutation.mutate()}
            sx={{ fontSize: '0.7rem' }}
            disabled={panicMutation.isLoading}
          >
            {panicMutation.isLoading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              'PANIC'
            )}
          </Button>
        </Stack>
      </Stack>

      {/* Search + Tab selector — search filters BOTH tabs by pair title */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexShrink: 0 }}>
        <TextField
          size="small"
          placeholder="Search by title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            width: 240,
            '& .MuiOutlinedInput-root': { fontSize: '0.75rem', height: 28 },
            '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
          }}
          inputProps={{ 'aria-label': 'Search pairs by title' }}
        />
        <Tabs
          value={tab}
          onChange={(_, v: 'a' | 'b') => setTab(v)}
          sx={{
            minHeight: 32,
            '& .MuiTab-root': { minHeight: 32, py: 0.5, fontSize: '0.75rem' }
          }}
        >
          <Tab value="a" label="Polymarket YES + Kalshi NO" />
          <Tab value="b" label="Polymarket NO + Kalshi YES" />
        </Tabs>
        {search.trim() && (
          <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
            {visiblePairs.length}/{pairs.length} matched
          </Typography>
        )}
      </Stack>

      <Box sx={{ flex: 3, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !isConnected && pairs.length === 0 ? (
          // Only show the placeholder when there is genuinely nothing to
          // render (never connected, no cached pairs). A TRANSIENT WS drop
          // (socket.connected flaps false→true on every reconnect) must
          // NOT tear down the table: the pair list is react-query-cached
          // and the live-state Map persists in useEntrySocket, so the rows
          // stay put. Unmounting here was what made the whole list vanish
          // for ~1-2s and then reappear reordered (fresh mount reset the
          // sort + a new opening_state snapshot re-sorted). The header
          // "Live / Disconnected" chip already signals connection state.
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
              Disconnected — waiting for opener backend.
            </Typography>
          </Box>
        ) : (
          <EntryTabByPath
            path={tab}
            pairs={visiblePairs}
            rules={sumRules}
            anyRulesByPairId={anyRulesByPairId}
            store={store}
            onCreateRule={handleCreateSumRule}
            onSetMakerFlag={handleSetMakerFlag}
            onUpdateRule={handleUpdateRule}
            onHidePair={handleHidePair}
            onOpenEnginePosition={handleOpenEnginePosition}
          />
        )}
      </Box>

      <EnginePositionDrawer
        open={enginePos !== null}
        onClose={handleCloseEnginePosition}
        believedPosition={
          enginePos ? enginePosState?.believedPosition ?? null : null
        }
        pairName={enginePos?.pairName}
      />
    </Box>
  );
};
